import { all, fork, put, takeEvery, call } from "redux-saga/effects";

// apicore
import { APICore, setAuthorization } from "../../helpers/api/apiCore";

// helpers
import {
  login as loginApi,
  logout as logoutApi,
  signup as signupApi,
  forgotPassword as forgotPasswordApi,
  getProfile as getProfileApi,
} from "../../helpers/api/auth";

// actions
import { authApiResponseSuccess, authApiResponseError } from "./actions";

// constants
import { AuthActionTypes } from "./constants";

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({ payload: { username, password }, type }) {
  try {
    const response = yield call(loginApi, { username, password });
    
    console.log("Login API Response:", response); // Debug log
    
    // Check if response exists
    if (!response) {
      throw new Error("No response received from server");
    }
    
    // Handle Go Fiber API response format
    if (response.data && response.data.status === 200) {
      const userData = response.data.data; // Go Fiber returns data in data.data
      
      // Set user session with tokens
      api.setLoggedInUser(userData);
      
      // Get user profile after successful login
      try {
        const profileResponse = yield call(getProfileApi);
        if (profileResponse && profileResponse.data && profileResponse.data.status === 200) {
          localStorage.setItem('user_profile', JSON.stringify(profileResponse.data.data));
        }
      } catch (profileError) {
        console.warn("Could not fetch user profile:", profileError);
      }
      
      yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, {
        token: userData.access_token,
        user: userData.user || { email: username }, // fallback user data
        ...userData
      }));
      
      // Dispatch custom event to trigger menu refresh
      window.dispatchEvent(new CustomEvent('userLoggedIn', { 
        detail: { user: userData.user || { email: username } } 
      }));
    } else {
      // Handle different response formats
      if (response.data) {
        throw new Error(response.data.message || "Login failed");
      } else {
        throw new Error("Invalid response format");
      }
    }  } catch (error) {
    console.error("Login error details:", {
      error: error,
      message: error.message,
      response: error.response,
      stack: error.stack
    });
    
    // Extract meaningful error message
    let errorMessage = "Login failed. Please try again.";
    
    if (error.response) {
      // API responded with error status
      console.log("Error response data:", error.response.data);
      if (error.response.data && error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.status === 401) {
        errorMessage = "Invalid email or password. Please check your credentials.";
      } else if (error.response.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, errorMessage));
    api.setLoggedInUser(null);
  }
}

/**
 * Logout the user
 */
function* logout() {
  try {
    yield call(logoutApi);
    api.setLoggedInUser(null);
    setAuthorization(null);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.LOGOUT_USER, error));
  }
}

function* signup({ payload: { fullname, email, password } }) {
  try {
    const response = yield call(signupApi, { fullname, email, password });
    const user = response.data;
    // api.setLoggedInUser(user);
    // setAuthorization(user['token']);
    yield put(authApiResponseSuccess(AuthActionTypes.SIGNUP_USER, user));
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.SIGNUP_USER, error));
    api.setLoggedInUser(null);
    setAuthorization(null);
  }
}

function* forgotPassword({ payload: { username } }) {
  try {
    const response = yield call(forgotPasswordApi, { username });
    yield put(
      authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response.data)
    );
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, error));
  }
}

export function* watchLoginUser() {
  yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

export function* watchLogout() {
  yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}

export function* watchSignup() {
  yield takeEvery(AuthActionTypes.SIGNUP_USER, signup);
}

export function* watchForgotPassword() {
  yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchLogout),
    fork(watchSignup),
    fork(watchForgotPassword),
  ]);
}

export default authSaga;
